import { makeStyles } from "@fluentui/react-components";
import * as FC from "@fluentui/react-components";
import * as IC from "@fluentui/react-icons";
import { useState, useEffect, useCallback, useContext, useRef } from "react";
import { TurbineDef, listTurbines } from "../../services/turbine_service";
import { useDrawer } from "../../components/use_drawer";
import { DeleteButton } from "../../components/delete_button";
import { ModelTurbineDropdown } from "../../services/model_turbine_service";
import * as FDP from "@fluentui/react-datepicker-compat";
import { toDate, toDateString } from "../../date_utils";
import { SimpleGrid } from "../../components/simple_grid";
import { bindInputValue } from "../../form_utils";
import { apiClient } from "../../services/api";
import { Context } from "../../context";
import cssStyles from "./turbine.module.css";

const useStyles = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("10px"),
        ...FC.shorthands.padding("10px"),
    },
    form: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("10px"),
    },
});

const toContextId = (value) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }

    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? id : null;
};

export const resolveTurbineManagementContext = ({ tenantId, fieldId, currentField }) => {
    const hasTenantId = tenantId !== null && tenantId !== undefined && tenantId !== "";
    const hasFieldId = fieldId !== null && fieldId !== undefined && fieldId !== "";
    const locked = hasTenantId || hasFieldId;

    if (!locked) {
        return {
            locked: false,
            tenantId: null,
            fieldId: toContextId(currentField?.id),
            error: null,
        };
    }

    const normalizedTenantId = toContextId(tenantId);
    const normalizedFieldId = toContextId(fieldId);
    if (!normalizedTenantId || !normalizedFieldId) {
        return {
            locked: true,
            tenantId: normalizedTenantId,
            fieldId: normalizedFieldId,
            error: "Embedded turbine management requires valid tenantId and fieldId.",
        };
    }

    return {
        locked: true,
        tenantId: normalizedTenantId,
        fieldId: normalizedFieldId,
        error: null,
    };
};

// The API client surfaces the backend error code both as `errorCode` and, because
// { error, message } puts the code first, as `message`. Raw codes were being alerted at
// the user ("保存失败: TurbineModelFieldMismatch"), which says nothing about what to do.
const TURBINE_WRITE_ERROR_MESSAGES = {
    TurbineIdentityRequired: "请填写风机编号和名称。",
    TurbineRelationRequired: "请选择风机型号，并确认已选定风场。",
    TurbineModelFieldMismatch:
        "所选风机型号已不存在或不可用，请刷新后重新选择。",
    FieldItemNotFound: "当前风场不存在或已停用，请返回风场列表重新选择。",
    TurbineIdentifierAlreadyExists: "该风机编号在本风场已被占用，请换一个编号。",
    TurbineIdentifierReservedByArchivedRecord:
        "该风机编号被本风场的一条已归档记录占用。请恢复该记录或换一个编号。",
    TurbineArchived: "该风机已归档，需先恢复才能修改。",
    TurbineRangeInvalid: "请输入有效的纯数字起始编号和结束编号，且结束编号不能小于起始编号。",
    TurbineRangeTooLarge: "一次最多批量添加 500 台风机，请缩小编号区间。",
    TurbineRangeIdentifierConflict: "编号区间内存在已使用或已归档保留的风机编号，请调整区间。",
};

export const describeTurbineWriteError = (error) => {
    const code = error?.errorCode || error?.message;
    const mapped = TURBINE_WRITE_ERROR_MESSAGES[code];
    if (mapped) {
        return mapped;
    }
    return error?.message || String(error ?? "未知错误");
};

export const buildTurbineRangePreview = (startSeq, endSeq) => {
    const startText = String(startSeq ?? "").trim();
    const endText = String(endSeq ?? "").trim();
    if (!/^\d+$/.test(startText) || !/^\d+$/.test(endText) ||
        startText.length > 255 || endText.length > 255) {
        return { error: "起始编号和结束编号必须是纯数字，且不能超过 255 位。", sequences: [] };
    }

    const start = window.BigInt(startText);
    const end = window.BigInt(endText);
    if (end < start) {
        return { error: "结束编号不能小于起始编号。", sequences: [] };
    }
    const count = end - start + 1n;
    if (count > 500n) {
        return { error: "一次最多批量添加 500 台风机。", sequences: [] };
    }

    const width = Math.max(startText.length, endText.length);
    const sequences = Array.from({ length: Number(count) }, (_, index) =>
        String(start + window.BigInt(index)).padStart(width, "0"));
    return { error: null, sequences };
};

export const TurbineRangeForm = ({ onChange, tenantId, fieldId, lockField = false }) => {
    const [startSeq, setStartSeq] = useState("");
    const [endSeq, setEndSeq] = useState("");
    const [modelId, setModelId] = useState(null);
    const [saving, setSaving] = useState(false);
    const preview = buildTurbineRangePreview(startSeq, endSeq);
    const canSave = tenantId && fieldId && modelId && !preview.error && preview.sequences.length > 0;

    const doSave = async () => {
        if (!canSave || saving) return;
        const payload = {
            StartSeq: startSeq.trim(),
            EndSeq: endSeq.trim(),
            ModelId: Number(modelId),
            FieldItemId: Number(fieldId),
        };

        setSaving(true);
        try {
            const result = lockField
                ? await apiClient.createManagedTurbineRange(tenantId, fieldId, payload)
                : await apiClient.createTurbineRange(payload, tenantId);
            alert(`已成功添加 ${result.createdCount ?? preview.sequences.length} 台风机。`);
            onChange();
        } catch (error) {
            console.error("Failed to create turbine range:", error);
            alert("批量添加失败: " + describeTurbineWriteError(error));
        } finally {
            setSaving(false);
        }
    };

    const first = preview.sequences[0];
    const last = preview.sequences[preview.sequences.length - 1];
    return (
        <div className={useStyles().form}>
            <FC.Label htmlFor="turbine-range-start">起始编号</FC.Label>
            <FC.Input
                id="turbine-range-start"
                value={startSeq}
                placeholder="例如 001"
                onChange={(_, data) => setStartSeq(data.value)}
            />
            <FC.Label htmlFor="turbine-range-end">结束编号</FC.Label>
            <FC.Input
                id="turbine-range-end"
                value={endSeq}
                placeholder="例如 020"
                onChange={(_, data) => setEndSeq(data.value)}
            />
            <FC.Label>统一风机型号</FC.Label>
            <ModelTurbineDropdown
                onOptionSelect={(_, data) => setModelId(Number(data.optionValue))}
            />
            {(startSeq || endSeq) && (
                <FC.MessageBar intent={preview.error ? "error" : "info"}>
                    <FC.MessageBarBody>
                        {preview.error || `将创建 ${preview.sequences.length} 台风机：${first} 至 ${last}，名称与编号相同。`}
                    </FC.MessageBarBody>
                </FC.MessageBar>
            )}
            <FC.Divider />
            <FC.Button appearance="primary" disabled={!canSave || saving} onClick={doSave}>
                {saving ? "正在添加..." : "确认批量添加"}
            </FC.Button>
        </div>
    );
};

export const TurbineForm = ({ item, onChange, tenantId, fieldId, lockField = false }) => {
    const { currentField } = useContext(Context);
    const [info, setInfo] = useState({});
    const v = bindInputValue(item, info, setInfo);
    const targetFieldId = toContextId(fieldId) ?? toContextId(currentField?.id);
    const doDelete = async () => {
        try {
            if (!tenantId) {
                alert("A target tenant is required before changing a turbine.");
                return;
            }
            if (lockField) {
                await apiClient.deleteManagedTurbine(tenantId, targetFieldId, item.id);
            } else {
                await apiClient.deleteTurbine(item.id, tenantId);
            }
            onChange();
        } catch (error) {
            console.error("Failed to delete turbine:", error);
            alert("删除失败: " + error.message);
        }
    };
    const doSave = async () => {
        try {
            if (!tenantId) {
                alert("A target tenant is required before changing a turbine.");
                return;
            }
            console.log("doSave called, item:", item, "info:", info);
            if (item) {
                // Update existing turbine (only send changed fields)
                const payload = {};

                if (info?.seq !== undefined) {
                    payload.Seq = info.seq;
                }
                if (info?.name !== undefined) {
                    payload.Name = info.name;
                }
                if (info?.model !== undefined) {
                    const modelId = Number(info.model);
                    if (Number.isNaN(modelId)) {
                        alert("请选择有效的风机型号");
                        return;
                    }
                    payload.ModelId = modelId;
                }
                if (lockField && targetFieldId) {
                    payload.FieldItemId = targetFieldId;
                } else if (info?.field_item !== undefined || info?.fieldItemId !== undefined) {
                    const fieldId = Number(info.field_item ?? info.fieldItemId);
                    if (!Number.isNaN(fieldId)) {
                        payload.FieldItemId = fieldId;
                    }
                }
                if (info?.installed_at !== undefined) {
                    const value = info.installed_at;
                    payload.InstalledAt = value instanceof Date ? value.toISOString() : value;
                }
                if (info?.description !== undefined) {
                    payload.Description = info.description;
                }

                if (Object.keys(payload).length === 0) {
                    alert("请修改后再保存。");
                    return;
                }

                if (lockField) {
                    await apiClient.updateManagedTurbine(
                        tenantId,
                        targetFieldId,
                        item.id,
                        payload,
                    );
                } else {
                    await apiClient.updateTurbine(item.id, payload, tenantId);
                }
                onChange();
            } else {
                // Embedded callers pin fieldId; the standalone page falls back to currentField.
                if (!targetFieldId) {
                    alert("请先选择一个风场");
                    return;
                }
                
                console.log("Current field from Context:", currentField);
                
                const modelId = info?.model != null ? Number(info.model) : null;

                const payload = {
                    Seq: info?.seq || '',
                    Name: info?.name || '',
                    FieldItemId: targetFieldId,
                    ModelId: modelId && !Number.isNaN(modelId) ? modelId : null,
                    InstalledAt: info?.installed_at ? info.installed_at.toISOString() : null,
                    Description: info?.description || null,
                };
                console.log("Creating turbine with data:", payload);

                if (!payload.ModelId) {
                    alert("请选择风机型号");
                    return;
                }

                if (lockField) {
                    await apiClient.createManagedTurbine(tenantId, targetFieldId, payload);
                } else {
                    await apiClient.createTurbine(payload, tenantId);
                }
                onChange();
            }
        } catch (error) {
            console.error("Failed to save turbine:", error);
            alert("保存失败: " + describeTurbineWriteError(error));
        }
    };

    let styles = useStyles();
    return (
        <div className={styles.form}>
            <FC.Input contentBefore={"编号"} {...v("seq")} />
            <FC.Input contentBefore={"名称"} {...v("name")} />
            <FC.Label>风机型号</FC.Label>
            {/*
              No fieldId: turbine models are a globally shared catalogue since 2026-08-14, so
              every field picks from the same list. The scoping added on 08-13 was the correct
              fix while the backend still demanded a same-field model; keeping it now would hide
              legitimate models and re-create the starvation it was working around.
            */}
            <ModelTurbineDropdown
                defaultValue={item?.model?.seq}
                defaultSelectedOptions={
                    item?.model?.id ?? item?.modelId
                        ? [String(item?.model?.id ?? item?.modelId)]
                        : undefined
                }
                onOptionSelect={(_, data) => {
                    const numericModelId = Number(data.optionValue);
                    setInfo({
                        ...info,
                        model: Number.isNaN(numericModelId)
                            ? data.optionValue
                            : numericModelId,
                    });
                }}
            />
            {/*
            <FC.Label>风场</FC.Label>
            <FieldItemDropdown
                defaultValue={item?.field_item?.name}
                onOptionSelect={(_, data) => {
                    setInfo({ ...info, field_item: data.optionValue });
                }}
            />
            */}
            <FC.Label>安装日期</FC.Label>
            <FDP.DatePicker
                formatDate={toDateString}
                value={
                    info.installed_at
                        ? toDate(info.installed_at)
                        : item?.installed_at
                        ? toDate(item.installed_at)
                        : null
                }
                onSelectDate={(date) =>
                    setInfo({ ...info, installed_at: date })
                }
            />
            <FC.Label>备注</FC.Label>
            <FC.Textarea {...v("description")}></FC.Textarea>
            <FC.Divider />
            {item && <DeleteButton onClick={doDelete} />}
            <FC.Button
                appearance="primary"
                onClick={doSave}
                disabled={Object.keys(info ?? {}).length === 0}
            >
                {item ? "保存" : "创建"}
            </FC.Button>
        </div>
    );
};

export const TurbineManagement = ({ tenantId = null, fieldId = null }) => {
    const { currentField, user } = useContext(Context);
    const managementContext = resolveTurbineManagementContext({
        tenantId,
        fieldId,
        currentField,
    });
    const [lockedField, setLockedField] = useState(null);
    const [contextError, setContextError] = useState(managementContext.error);
    let [turbines, setTurbines] = useState([]);
    let [filter, setFilter] = useState(null);
    let { openDrawer, closeDrawer, drawer } = useDrawer();
    const turbineRequestSequence = useRef(0);

    useEffect(() => {
        let cancelled = false;
        turbineRequestSequence.current += 1;

        if (!managementContext.locked) {
            setLockedField(null);
            setContextError(null);
            return () => {
                cancelled = true;
            };
        }

        setLockedField(null);
        setTurbines([]);
        if (managementContext.error) {
            setContextError(managementContext.error);
            return () => {
                cancelled = true;
            };
        }

        setContextError(null);
        apiClient.getFieldItems({ tenantId: managementContext.tenantId })
            .then((fields) => {
                if (cancelled) {
                    return;
                }

                const field = (fields || []).find(
                    (candidate) => toContextId(candidate?.id) === managementContext.fieldId,
                );
                if (!field) {
                    setContextError(
                        `Field ${managementContext.fieldId} is not available in tenant ${managementContext.tenantId}.`,
                    );
                    return;
                }

                setLockedField(field);
            })
            .catch((error) => {
                if (!cancelled) {
                    console.error("Failed to validate embedded turbine context:", error);
                    setContextError("Unable to validate the embedded tenant and field context.");
                }
            });

        return () => {
            cancelled = true;
        };
    }, [
        managementContext.locked,
        managementContext.tenantId,
        managementContext.fieldId,
        managementContext.error,
    ]);

    const effectiveFieldId = managementContext.locked
        ? toContextId(lockedField?.id)
        : managementContext.fieldId;
    const writeTenantId = managementContext.locked
        ? managementContext.tenantId
        : toContextId(user?.tenantId);

    const loadTurbines = useCallback(async () => {
        if (managementContext.locked && (!effectiveFieldId || contextError)) {
            return;
        }

        const requestSequence = ++turbineRequestSequence.current;
        try {
            let rets = await listTurbines(null, filter, effectiveFieldId);
            if (requestSequence !== turbineRequestSequence.current) {
                return;
            }
            console.log(`[TURBINE DEBUG] Loaded ${rets.length} turbines for field ${effectiveFieldId}`);
            setTurbines(rets);
        } catch (error) {
            if (requestSequence !== turbineRequestSequence.current) {
                return;
            }
            console.error("Failed to load turbines:", error);
            setTurbines([]);
        }
    }, [filter, effectiveFieldId, managementContext.locked, contextError]);

    useEffect(() => {
        loadTurbines();
    }, [loadTurbines]);

    useEffect(() => () => {
        turbineRequestSequence.current += 1;
    }, []);

    const columns = [
        TurbineDef.createTableColumn("seq"),
        TurbineDef.createTableColumn("name"),
        TurbineDef.createTableColumn("model"),
        TurbineDef.createTableColumn("field_item"),
        TurbineDef.createTableColumn("installed_at"),
        TurbineDef.createTableColumn("updated_at"),
        TurbineDef.createTableColumn("description"),
        TurbineDef.createOpsColumn({
            renderCell: (item) => {
                const onClick = () => {
                    openDrawer({
                        header: "风机详情",
                        body: (
                            <TurbineForm
                                item={item}
                                onChange={onFormChange}
                                tenantId={writeTenantId}
                                fieldId={effectiveFieldId}
                                lockField={managementContext.locked}
                            />
                        ),
                    });
                };
                return (
                    <FC.TableCellLayout>
                        <FC.Button
                            onClick={onClick}
                            appearance="subtle"
                            icon={<IC.MoreCircleFilled />}
                        />
                    </FC.TableCellLayout>
                );
            },
        }),
    ];
    const onFormChange = () => {
        loadTurbines();
        closeDrawer();
    };
    const onNewTurbine = () => {
        openDrawer({
            header: "创建风机",
            body: (
                <TurbineForm
                    turbine={null}
                    onChange={onFormChange}
                    tenantId={writeTenantId}
                    fieldId={effectiveFieldId}
                    lockField={managementContext.locked}
                />
            ),
        });
    };
    const onNewTurbineRange = () => {
        openDrawer({
            header: "按编号区间批量添加风机",
            body: (
                <TurbineRangeForm
                    onChange={onFormChange}
                    tenantId={writeTenantId}
                    fieldId={effectiveFieldId}
                    lockField={managementContext.locked}
                />
            ),
        });
    };
    const handleBeginImport = () => {
        const f = document.getElementById("importTurbineFile");
        f.click();
    };
    const handleFileChange = async (event) => {
        console.log("handleFileChange, event=", event);
        alert("导入功能暂时不可用，需要更新到新的API");
        return;
    };
    let styles = useStyles();
    return (
        <div className={styles.root}>
            {managementContext.locked && (
                <FC.MessageBar intent={contextError ? "error" : "info"}>
                    <FC.MessageBarBody>
                        {contextError || (lockedField
                            ? `Locked context: tenant ${managementContext.tenantId} / ${lockedField.name || `field ${effectiveFieldId}`}`
                            : "Validating locked tenant and field context...")}
                    </FC.MessageBarBody>
                </FC.MessageBar>
            )}
            {!managementContext.locked && !writeTenantId && (
                <FC.MessageBar intent="warning">
                    <FC.MessageBarBody>
                        This legacy page has no explicit target tenant. Open it from the managed
                        tenant workbench before creating, updating or archiving turbines.
                    </FC.MessageBarBody>
                </FC.MessageBar>
            )}
            <div
                style={{
                    display: "flex",
                    gap: 10,
                    flexDirection: "row",
                    alignItems: "center",
                }}
            >
                <FC.Label>快速检索</FC.Label>
                <FC.Input
                    contentBefore={<IC.SearchRegular />}
                    placeholder="按风机名称搜索..."
                    onChange={(evt) => setFilter(evt.target.value)}
                    style={{ flex: 1 }}
                />
                <FC.Button
                    onClick={onNewTurbineRange}
                    disabled={managementContext.locked && (!effectiveFieldId || !!contextError)}
                >
                    批量添加
                </FC.Button>
                <FC.Button
                    appearance="primary"
                    onClick={onNewTurbine}
                    disabled={managementContext.locked && (!effectiveFieldId || !!contextError)}
                >
                    添加风机
                </FC.Button>
            </div>
            <div className="flex pr-[128px]">
                <div className="flex-1"></div>
                <FC.Link as="a" href="/import_turbine_template.xlsx">
                    下载导入模板
                </FC.Link>
                <div className="w-[16px]"></div>
                <FC.Button onClick={handleBeginImport}>
                    上传文件批量导入...
                </FC.Button>
                <input
                    type="file"
                    id="importTurbineFile"
                    className="hidden"
                    onChange={handleFileChange}
                />
            </div>
            <div
                className={cssStyles.scrollContainer}
                style={{
                    maxHeight: '800px',
                    height: '800px',
                    overflowY: 'scroll',
                    overflowX: 'hidden',
                    border: '1px solid #e0e0e0',
                    display: 'block',
                    position: 'relative'
                }}
            >
                <SimpleGrid
                    items={turbines}
                    columns={columns}
                />
            </div>
            {drawer}
        </div>
    );
};
